<template>
    <div class="hero-carousel-block carousel fullwidthwrapperblock" ref="heroCarousel" aria-live="off">
        <div class="hero-carousel-block__wrapper carouselcontent" v-bind:style="carouselTransform" v-html="slidesContent">
        </div>
        <div class="carouselcontrols" v-if="numberOfItems > 1">
            <div class="hero-carousel-block__arrow-wrapper" v-on:click="handleArrowLeftClick">
                <button type="button" class="controlleft" :aria-label="moveLeftLabel" v-html="arrowLeftContent">
                </button>
            </div>
            <div class="hero-carousel-block__arrow-wrapper" v-on:click="handleArrowRightClick">
                <button type="button" class="controlright" :aria-label="moveRightLabel" v-html="arrowRightContent">
                </button>
            </div>
        </div>
        <div class="paging-indicators text-center" v-if="numberOfItems > 1">
            <div class="indicators" role="tablist" :aria-label="slidesLabel">
                <button v-for="(n, i) in parseInt(numberOfItems)"
                        :key="i"
                        class="indicator indicator--white"
                        v-bind:class="{'active': i === index}"
                        v-on:click="changeSlide(i)"
                        :aria-label="slideTranslation + ' ' + parseInt(i+1)"
                        role="tab"
                        :aria-selected="i === index ? 'true' : 'false'"
                        :aria-controls="'carousel-item-' + parseInt(i+1)">
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
    import Swipe from '../../utils/Swipe.js';

    const props = defineProps({
        numberOfItems: Number,
        autoplaySpeed: Number,
        slideTranslation: String,
        moveLeftLabel: String,
        moveRightLabel: String,
        slidesLabel: String,
        initialSlidesContent: String,
        initialArrowLeftContent: String,
        initialArrowRightContent: String
    });

    const heroCarousel = ref(null);
    const index = ref(0);
    const autoplay = ref(null);
    const innerBlocks = ref([]);

    const slidesContent = ref(props.initialSlidesContent || '');
    const arrowLeftContent = ref(props.initialArrowLeftContent || '');
    const arrowRightContent = ref(props.initialArrowRightContent || '');

    const instance = getCurrentInstance();

    const transitionSpeed = ref(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--carousel-transition-speed')
            .slice(0, -1) * 1000
    );

    const carouselTransform = computed(() => {
        return { transform: 'translateX(-' + index.value * 100 + '%)' };
    });

    const scrolledToTheEnd = computed(() => {
        return props.numberOfItems > 0 && index.value >= (props.numberOfItems - 1);
    });

    const scrolledToTheStart = computed(() => {
        return index.value < 1;
    });

    const blockElementID = computed(() => {
        return instance?.proxy?.$el?.id || '';
    });

    // Methods
    const scrollPrev = () => {
        if (!scrolledToTheStart.value) {
            index.value--;
        } else {
            index.value = props.numberOfItems - 1;
        }
    };

    const scrollNext = () => {
        if (!scrolledToTheEnd.value) {
            index.value++;
        } else {
            index.value = 0;
        }
    };

    const handleArrowLeftClick = () => {
        scrollPrev();
        if (autoplay.value) {
            clearInterval(autoplay.value);
            autoplay.value = null;
        }
    };

    const handleArrowRightClick = () => {
        scrollNext();
        if (autoplay.value) {
            clearInterval(autoplay.value);
            autoplay.value = null;
        }
    };

    const changeSlide = (i) => {
        if (i !== index.value) {
            index.value = i;
            if (autoplay.value) {
                clearInterval(autoplay.value);
                autoplay.value = null;
            }
        }
    };

    const setupAutoplay = () => {
        if (props.autoplaySpeed && props.autoplaySpeed > 0) {
            autoplay.value = setInterval(() => scrollNext(), props.autoplaySpeed * 1000);
        }
    };

    const initializeCarousel = () => {
        if (heroCarousel.value) {
            const swipeCarousel = new Swipe(heroCarousel.value)
                .onLeft(handleArrowRightClick)
                .onRight(handleArrowLeftClick);
            swipeCarousel.run();
        }

        setTimeout(() => {
            if (instance?.proxy?.$el) {
                innerBlocks.value = Array.from(instance.proxy.$el.querySelectorAll('.block'));

                if (innerBlocks.value.length > 0) {
                    innerBlocks.value.forEach((block, blockIndex) => {
                        if (block.classList.contains('herovideoblock')) {
                            var videoBlock = block.querySelector('.hero-video-block');
                            if (blockIndex > 0) {
                                const video = videoBlock?.querySelector('video');
                                if (video) {
                                    video.setAttribute('data-autoplay', video.autoplay);
                                    video.autoplay = false;
                                }
                            }
                            if (typeof window.initHeroVideo === 'function') {
                                window.initHeroVideo(videoBlock, true);
                            }
                        }
                    });

                    if (innerBlocks.value.length > 1) {
                        innerBlocks.value.forEach((block, i) => {
                            block.id = 'carousel-item-' + parseInt(i + 1);
                            block.setAttribute('role', 'tabpanel');
                            block.setAttribute('aria-roledescription', 'slide');
                            block.setAttribute('aria-label', props.slideTranslation + ' ' + (i + 1) + ' of ' + innerBlocks.value.length);
                        });
                    }
                }
            }
        }, 100);
    };

    // Lifecycle hooks
    onMounted(() => {
        document.addEventListener("readystatechange", (event) => {
            if (event.target.readyState === "complete") {
                setupAutoplay();
            }
        });
        initializeCarousel();
    });

    onBeforeUnmount(() => {
        if (autoplay.value) {
            clearInterval(autoplay.value);
        }
    });

    watch(index, () => {
        innerBlocks.value.forEach((block, i) => {
            if (block.classList.contains('herovideoblock')) {
                var videoBlock = block.querySelector('.hero-video-block');
                var videoElement = block.querySelector('video');
                if (videoElement) {
                    if (i === index.value) {
                        if (videoElement.getAttribute('data-autoplay') === 'true' ||
                            videoElement.getAttribute('data-carousel-paused') === 'true') {
                            videoElement.play();
                            videoElement.removeAttribute('data-carousel-paused');
                        }
                    } else {
                        if (videoBlock && videoBlock.classList.contains('playing')) {
                            videoElement.pause();
                            videoElement.setAttribute('data-carousel-paused', true);
                        }
                    }
                }
            }
            setTimeout(() => {
                block.setAttribute('aria-hidden', (i === index.value) ? 'false' : 'true');
            }, transitionSpeed.value);
        });
    });
</script>